/**
 * IB-755 — Budget Versions v2.
 *
 * Client-side version-to-version comparison. The backend returns only each
 * version's raw figures; the movement shown against every money cell (and the
 * hero comparison tile) is derived here from the versions already fetched, so
 * the comparison dropdown re-bases the whole list instantly with no reload.
 *
 * There are only ever a handful of versions per event, so operating over the
 * fetched page in memory is cheap.
 */

import { COMPARISON_MODE_PRIOR } from './ComparisonSelector';

// Shown for the earliest version when comparing to the previous version (it has
// no prior), and for the reference version itself when a specific version is
// selected (it is the basis, so there is no movement to show).
export const SENTINEL_NEW = '— new';
export const SENTINEL_REFERENCE = '—';

/**
 * Signed percentage change guarded against a zero base. Returns null when the
 * base is zero so the caller renders the value alone (no %).
 */
function percentage(diff, base) {
    if (!base) {
        return null;
    }

    return (diff / Math.abs(base)) * 100;
}

/**
 * The movement of `row` measured against `base`, mirroring the semantic
 * direction rules the list uses: rising revenue/net is positive (green), rising
 * expenditure is positive-for-red (i.e. flagged so the cell colours it red).
 */
function diffMetrics(row, base) {
    const revenueDiff = row.revenue - base.revenue;
    const expenditureDiff = row.expenditure - base.expenditure;
    const netDiff = row.net - base.net;

    return {
        revenue: revenueDiff,
        expenditure: expenditureDiff,
        net: netDiff,

        revenue_pct: percentage(revenueDiff, base.revenue),
        expenditure_pct: percentage(expenditureDiff, base.expenditure),
        net_pct: percentage(netDiff, base.net),

        // Semantic direction: rising revenue/net is good (green); rising
        // expenditure is bad (red). Flat movement is not flagged positive.
        revenue_is_positive: revenueDiff > 0,
        net_is_positive: netDiff > 0,
        expenditure_is_positive: expenditureDiff < 0,

        // Shape-of-budget deltas re-base with the same reference as the money
        // cells, so the Compare dropdown moves the whole row consistently (and
        // the selected reference row shows no shape delta). (IB-755)
        departments_count: (row.departments_count || 0) - (base.departments_count || 0),
        costcenters_count: (row.costcenters_count || 0) - (base.costcenters_count || 0),
        workforce_count: (row.workforce_count || 0) - (base.workforce_count || 0),
    };
}

/**
 * The versions eligible as comparison references / dropdown options, ordered by
 * version number ascending. `budgets` are the fetched list rows (each with a
 * `.metrics` block).
 */
export function orderedVersions(budgets) {
    return [...budgets].filter((budget) => budget.metrics).sort((a, b) => (a.metrics.version || 0) - (b.metrics.version || 0));
}

/**
 * Build the per-row comparison map keyed by budget id.
 *
 *  - mode = 'prior' (default): each row compares to its own immediately-
 *    preceding version by version order; the earliest row → SENTINEL_NEW.
 *  - mode = a version id: every row compares to that version's figures; the row
 *    that IS the reference → SENTINEL_REFERENCE.
 *
 * @returns {Object<string|number, object|string>}
 */
export function buildComparisons(budgets, mode) {
    const ordered = orderedVersions(budgets);
    const comparisons = {};

    if (mode === COMPARISON_MODE_PRIOR) {
        ordered.forEach((budget, index) => {
            if (index === 0) {
                comparisons[budget.id] = SENTINEL_NEW;
                return;
            }

            comparisons[budget.id] = diffMetrics(budget.metrics, ordered[index - 1].metrics);
        });

        return comparisons;
    }

    // Specific reference version selected.
    const reference = ordered.find((budget) => String(budget.id) === String(mode));

    ordered.forEach((budget) => {
        if (!reference || String(budget.id) === String(reference.id)) {
            comparisons[budget.id] = SENTINEL_REFERENCE;
            return;
        }

        comparisons[budget.id] = diffMetrics(budget.metrics, reference.metrics);
    });

    return comparisons;
}

/**
 * Build the per-row DUAL comparison map keyed by budget id. Each entry exposes
 * BOTH movements the money cells need (IB-757 v2 polish):
 *
 *  - `prior`: movement vs the immediately-preceding version (by version order) —
 *    ALWAYS present; the earliest row is the SENTINEL_NEW string.
 *  - `base`: movement vs the dropdown-selected base version — present only when a
 *    specific version is selected (null in the default "Previous version" mode,
 *    where a "vs base" row would just duplicate "vs prior"); the selected base
 *    row itself is the SENTINEL_REFERENCE string.
 *
 * @returns {Object<string|number, { prior: object|string, base: object|string|null }>}
 */
export function buildDualComparisons(budgets, mode) {
    const prior = buildComparisons(budgets, COMPARISON_MODE_PRIOR);
    const base = mode === COMPARISON_MODE_PRIOR ? null : buildComparisons(budgets, mode);

    const combined = {};
    orderedVersions(budgets).forEach((budget) => {
        combined[budget.id] = {
            prior: prior[budget.id],
            base: base ? base[budget.id] : null,
        };
    });

    return combined;
}

/**
 * The version currently selected as the comparison base, for the hero Baseline
 * tile and the trend baseline-ref line.
 *
 * There is NO single base in the default "Previous version" mode (each row
 * compares to its own prior), so this returns null then — the Baseline tile
 * renders its "select a version" prompt and no baseline-ref line is drawn. When
 * a specific version is selected it returns that version's identity and figures.
 *
 * @returns {{ id: number, name: string, version: number, net: number, revenue: number, expenditure: number }|null}
 */
export function resolveReference(budgets, mode) {
    if (mode === COMPARISON_MODE_PRIOR) {
        return null;
    }

    const reference = orderedVersions(budgets).find((budget) => String(budget.id) === String(mode));

    if (!reference) {
        return null;
    }

    const { name, version, net, revenue, expenditure } = reference.metrics;

    return { id: reference.id, name, version, net, revenue, expenditure };
}
